import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useClientContext } from '@/contexts/ClientContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ScanSearch,
  Monitor,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Plus,
  Key,
  Settings2,
  Copy,
  Minus,
  Download,
  ChevronDown,
  AppWindow,
  Terminal,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { EditAtivoDialog } from '@/components/Ativos/EditAtivoDialog';
import { useAtivoById } from '@/hooks/useAtivos';
import { SoftwareReconciliation } from './SoftwareReconciliation';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Schedule {
  type: 'daily' | 'weekly' | 'custom_days' | 'monthly' | 'interval';
  time?: string;       // HH:MM
  days?: number[];     // 0=seg … 6=dom
  day?: number;        // dia do mês (monthly)
  minutes?: number;    // interval
}

interface ApiToken {
  id: string;
  label: string;
  key_prefix: string;
  is_active: boolean;
  last_used_at: string | null;
  schedule: Schedule;
  created_at: string;
}

interface DiscoveryDevice {
  device_id: string;
  ativo_id: string;
  last_seen_at: string | null;
  agent_version: string | null;
  wmi_method: string | null;
  cmdb_ativos: {
    nome: string;
    numero_serie: string | null;
    sistema_operacional: string | null;
    fabricantes: { nome: string } | null;
  } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAYS_PT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function hoursAgo(iso: string | null): number {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}

function deviceStatusBadge(lastSeenAt: string | null) {
  const h = hoursAgo(lastSeenAt);
  if (h < 26)  return <Badge className="bg-green-100 text-green-700 border-green-200">Sincronizado</Badge>;
  if (h < 168) return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Atrasado</Badge>;
  if (h < Infinity) return <Badge className="bg-red-100 text-red-700 border-red-200">Inativo</Badge>;
  return <Badge variant="secondary">Nunca</Badge>;
}

function scheduleLabel(s: Schedule): string {
  const labels: Record<string, string> = {
    daily: 'Diário',
    weekly: 'Semanal',
    custom_days: 'Dias específicos',
    monthly: 'Mensal',
    interval: 'Intervalo',
  };
  const base = labels[s.type] ?? s.type;
  if (s.type === 'interval') return `${base} (${s.minutes ?? 1440} min)`;
  if (s.time) return `${base} às ${s.time}`;
  return base;
}

// ─── Download URLs (Supabase Storage — bucket agent-downloads) ────────────────

const STORAGE_BASE = 'https://idctavngatyyileoitgz.supabase.co/storage/v1/object/public/agent-downloads';

const AGENT_DOWNLOADS = [
  {
    os: 'Windows',
    label: 'Windows (.exe)',
    icon: 'windows',
    file: 'aroan-agent-setup-1.0.0-b11.exe',
    description: 'Windows 10/11 · x64',
  },
  {
    os: 'Linux DEB',
    label: 'Linux Ubuntu/Debian (.deb)',
    icon: 'linux',
    file: 'aroan-agent_1.0.0_amd64.deb',
    description: 'Ubuntu 20.04+ · Debian 11+ · x64',
  },
  {
    os: 'Linux RPM',
    label: 'Linux RHEL/CentOS/Fedora (.rpm)',
    icon: 'linux',
    file: 'aroan-agent-1.0.0.x86_64.rpm',
    description: 'RHEL 8+ · CentOS 8+ · Fedora 36+ · x64',
  },
] as const;

async function sha256hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function generateToken(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(24));
  const b64 = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  return `tk_${b64}`;
}

// ─── Schedule Editor ──────────────────────────────────────────────────────────

function ScheduleEditor({
  value,
  onChange,
}: {
  value: Schedule;
  onChange: (s: Schedule) => void;
}) {
  const toggleDay = (d: number) => {
    const days = value.days ?? [];
    onChange({
      ...value,
      days: days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort(),
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>Tipo de agendamento</Label>
        <Select
          value={value.type}
          onValueChange={type =>
            onChange({ type: type as Schedule['type'], time: '02:00', days: [], day: 1, minutes: 1440 })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Diário</SelectItem>
            <SelectItem value="weekly">Semanal</SelectItem>
            <SelectItem value="custom_days">Dias específicos</SelectItem>
            <SelectItem value="monthly">Mensal</SelectItem>
            <SelectItem value="interval">Intervalo personalizado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {value.type !== 'interval' && (
        <div className="space-y-1">
          <Label>Horário (hora local do agente)</Label>
          <Input
            type="time"
            value={value.time ?? '02:00'}
            onChange={e => onChange({ ...value, time: e.target.value })}
          />
        </div>
      )}

      {(value.type === 'weekly' || value.type === 'custom_days') && (
        <div className="space-y-1">
          <Label>Dias da semana</Label>
          <div className="flex gap-1 flex-wrap">
            {DAYS_PT.map((d, i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggleDay(i)}
                className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
                  (value.days ?? []).includes(i)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}

      {value.type === 'monthly' && (
        <div className="space-y-1">
          <Label>Dia do mês (1–28)</Label>
          <Input
            type="number"
            min={1}
            max={28}
            value={value.day ?? 1}
            onChange={e => onChange({ ...value, day: parseInt(e.target.value) || 1 })}
            className="w-24"
          />
        </div>
      )}

      {value.type === 'interval' && (
        <div className="space-y-1">
          <Label>Intervalo em minutos (mín. 5)</Label>
          <Input
            type="number"
            min={5}
            max={10080}
            value={value.minutes ?? 1440}
            onChange={e => onChange({ ...value, minutes: parseInt(e.target.value) || 1440 })}
            className="w-32"
          />
          <p className="text-xs text-gray-500">
            {((value.minutes ?? 1440) / 60).toFixed(1)} horas
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const AROANDiscovery: React.FC = () => {
  const { profile } = useAuth();
  const clientContext = useClientContext();
  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  const clientId = isAdmin
    ? (clientContext?.showAllClients ? null : clientContext?.currentClientId)
    : (profile as any)?.client_id;

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ── Dialog states ──────────────────────────────────────────────────────────
  const [scheduleDialog, setScheduleDialog] = useState<{
    open: boolean;
    tokenId: string;
    schedule: Schedule;
  }>({ open: false, tokenId: '', schedule: { type: 'daily', time: '02:00' } });

  const [newTokenDialog, setNewTokenDialog] = useState(false);
  const [newTokenLabel, setNewTokenLabel] = useState('');
  const [newTokenSchedule, setNewTokenSchedule] = useState<Schedule>({ type: 'daily', time: '02:00' });
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [editingAtivoId, setEditingAtivoId] = useState<string | null>(null);
  const { data: editingAtivoData, isLoading: ativoLoading } = useAtivoById(editingAtivoId);

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: devices = [], isLoading: devicesLoading, refetch: refetchDevices } = useQuery({
    queryKey: ['discovery-devices', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('discovery_device_map')
        .select(`
          device_id, ativo_id, last_seen_at, agent_version, wmi_method,
          cmdb_ativos ( nome, numero_serie, sistema_operacional, fabricantes ( nome ) )
        `)
        .eq('client_id', clientId)
        .order('last_seen_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as DiscoveryDevice[];
    },
    enabled: !!clientId,
    refetchInterval: 60_000,
  });

  const { data: tokens = [], isLoading: tokensLoading } = useQuery({
    queryKey: ['discovery-tokens', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('tenant_api_keys')
        .select('id, label, key_prefix, is_active, last_used_at, schedule, created_at')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as ApiToken[];
    },
    enabled: !!clientId,
  });

  // ── Stats ──────────────────────────────────────────────────────────────────
  const syncedToday  = devices.filter(d => hoursAgo(d.last_seen_at) < 26).length;
  const delayed      = devices.filter(d => { const h = hoursAgo(d.last_seen_at); return h >= 26 && h < 168; }).length;
  const inactive     = devices.filter(d => hoursAgo(d.last_seen_at) >= 168).length;
  const activeTokens = tokens.filter(t => t.is_active).length;

  // ── Mutations ──────────────────────────────────────────────────────────────
  const updateSchedule = useMutation({
    mutationFn: async ({ tokenId, schedule }: { tokenId: string; schedule: Schedule }) => {
      const { error } = await supabase
        .from('tenant_api_keys')
        .update({ schedule: schedule as any })
        .eq('id', tokenId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery-tokens', clientId] });
      setScheduleDialog(s => ({ ...s, open: false }));
      toast({ title: 'Agendamento atualizado com sucesso.' });
    },
    onError: () => toast({ title: 'Erro ao atualizar agendamento.', variant: 'destructive' }),
  });

  const toggleToken = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('tenant_api_keys')
        .update({ is_active })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['discovery-tokens', clientId] }),
    onError: () => toast({ title: 'Erro ao alterar token.', variant: 'destructive' }),
  });

  const createToken = useMutation({
    mutationFn: async ({ label, schedule }: { label: string; schedule: Schedule }) => {
      if (!clientId) throw new Error('Selecione um cliente primeiro.');
      const raw = generateToken();
      const key_hash = await sha256hex(raw);
      const key_prefix = raw.slice(0, 10);
      const { error } = await supabase.from('tenant_api_keys').insert({
        client_id: clientId,
        label,
        key_hash,
        key_prefix,
        schedule: schedule as any,
        interval_minutes: schedule.type === 'interval' ? (schedule.minutes ?? 1440) : 1440,
        is_active: true,
      });
      if (error) throw error;
      return raw;
    },
    onSuccess: raw => {
      queryClient.invalidateQueries({ queryKey: ['discovery-tokens', clientId] });
      setNewTokenDialog(false);
      setCreatedToken(raw);
      setNewTokenLabel('');
      setNewTokenSchedule({ type: 'daily', time: '02:00' });
    },
    onError: (e: Error) => toast({ title: e.message, variant: 'destructive' }),
  });

  // ─── No client selected ───────────────────────────────────────────────────
  if (isAdmin && !clientId) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <ScanSearch className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AROAN CMDB</h1>
            <p className="text-sm text-gray-500">Discovery automático de dispositivos</p>
          </div>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center h-40 text-gray-500">
            Selecione um cliente no seletor acima para visualizar os dados de Discovery.
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ScanSearch className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AROAN CMDB</h1>
            <p className="text-sm text-gray-500">Discovery automático de dispositivos</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Download do agente */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Baixar Agente
                <ChevronDown className="h-3 w-3 ml-1 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs text-gray-500 font-normal">
                Selecione o sistema operacional
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {AGENT_DOWNLOADS.map(d => (
                <DropdownMenuItem
                  key={d.file}
                  asChild
                >
                  <a
                    href={`${STORAGE_BASE}/${d.file}`}
                    download={d.file}
                    className="flex items-start gap-3 cursor-pointer"
                  >
                    {d.icon === 'windows'
                      ? <AppWindow className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />
                      : <Terminal  className="h-4 w-4 mt-0.5 text-orange-500 flex-shrink-0" />
                    }
                    <div>
                      <p className="text-sm font-medium">{d.label}</p>
                      <p className="text-xs text-gray-400">{d.description}</p>
                    </div>
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" size="sm" onClick={() => refetchDevices()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-2xl font-bold">{devices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-xs text-gray-500">Sincronizados</p>
                <p className="text-2xl font-bold text-green-600">{syncedToday}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-xs text-gray-500">Atrasados</p>
                <p className="text-2xl font-bold text-yellow-600">{delayed}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-xs text-gray-500">Tokens ativos</p>
                <p className="text-2xl font-bold text-blue-600">{activeTokens}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="dispositivos">
        <TabsList>
          <TabsTrigger value="dispositivos">Dispositivos ({devices.length})</TabsTrigger>
          <TabsTrigger value="tokens">Tokens de API ({tokens.length})</TabsTrigger>
          <TabsTrigger value="reconciliacao">Reconciliação de Software</TabsTrigger>
        </TabsList>

        {/* ── Dispositivos ── */}
        <TabsContent value="dispositivos">
          <Card>
            <CardContent className="p-0">
              {devicesLoading ? (
                <div className="flex items-center justify-center h-40 text-gray-400">
                  <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Carregando...
                </div>
              ) : devices.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
                  <ScanSearch className="h-8 w-8" />
                  <p>Nenhum dispositivo descoberto ainda.</p>
                  <p className="text-xs">Instale o agente AROAN nos dispositivos para iniciar o discovery.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Hostname</TableHead>
                      <TableHead>Sistema Operacional</TableHead>
                      <TableHead>Nº Série</TableHead>
                      <TableHead>Fabricante</TableHead>
                      <TableHead>Versão do Agente</TableHead>
                      <TableHead>Última Sync</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.map(d => (
                      <TableRow
                        key={d.device_id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => setEditingAtivoId(d.ativo_id)}
                      >
                        <TableCell className="font-medium hover:text-primary hover:underline">
                          {d.cmdb_ativos?.nome ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {d.cmdb_ativos?.sistema_operacional ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm font-mono text-gray-500">
                          {d.cmdb_ativos?.numero_serie ?? '—'}
                        </TableCell>
                        <TableCell>
                          {d.cmdb_ativos?.fabricantes?.nome ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {d.agent_version ?? '—'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(d.last_seen_at)}
                        </TableCell>
                        <TableCell>
                          {deviceStatusBadge(d.last_seen_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tokens ── */}
        <TabsContent value="tokens">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Tokens de API</CardTitle>
              <Button size="sm" onClick={() => setNewTokenDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Token
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {tokensLoading ? (
                <div className="flex items-center justify-center h-32 text-gray-400">
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" /> Carregando...
                </div>
              ) : tokens.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400 gap-1">
                  <Key className="h-6 w-6" />
                  <p className="text-sm">Nenhum token criado.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Prefixo</TableHead>
                      <TableHead>Agendamento</TableHead>
                      <TableHead>Último uso</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tokens.map(t => (
                      <TableRow key={t.id}>
                        <TableCell className="font-medium">{t.label}</TableCell>
                        <TableCell className="font-mono text-sm text-gray-500">
                          {t.key_prefix}…
                        </TableCell>
                        <TableCell className="text-sm">{scheduleLabel(t.schedule)}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {formatDate(t.last_used_at)}
                        </TableCell>
                        <TableCell>
                          {t.is_active
                            ? <Badge className="bg-green-100 text-green-700 border-green-200">Ativo</Badge>
                            : <Badge variant="secondary">Inativo</Badge>
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Editar agendamento"
                              onClick={() =>
                                setScheduleDialog({ open: true, tokenId: t.id, schedule: t.schedule })
                              }
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title={t.is_active ? 'Desativar token' : 'Ativar token'}
                              onClick={() => toggleToken.mutate({ id: t.id, is_active: !t.is_active })}
                            >
                              {t.is_active
                                ? <Minus className="h-4 w-4 text-red-500" />
                                : <CheckCircle2 className="h-4 w-4 text-green-500" />
                              }
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Reconciliação ── */}
        <TabsContent value="reconciliacao" className="mt-4">
          {clientId ? (
            <SoftwareReconciliation clientId={clientId as string} />
          ) : (
            <p className="text-sm text-gray-400 py-4">Selecione um cliente para ver a reconciliação.</p>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Dialog: Editar Agendamento ── */}
      <Dialog
        open={scheduleDialog.open}
        onOpenChange={o => setScheduleDialog(s => ({ ...s, open: o }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Agendamento</DialogTitle>
          </DialogHeader>
          <ScheduleEditor
            value={scheduleDialog.schedule}
            onChange={schedule => setScheduleDialog(s => ({ ...s, schedule }))}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialog(s => ({ ...s, open: false }))}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                updateSchedule.mutate({ tokenId: scheduleDialog.tokenId, schedule: scheduleDialog.schedule })
              }
              disabled={updateSchedule.isPending}
            >
              {updateSchedule.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Novo Token ── */}
      <Dialog open={newTokenDialog} onOpenChange={setNewTokenDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Criar Novo Token de API</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Identificação do token</Label>
              <Input
                placeholder="Ex: Agentes — Filial SP"
                value={newTokenLabel}
                onChange={e => setNewTokenLabel(e.target.value)}
              />
            </div>
            <ScheduleEditor value={newTokenSchedule} onChange={setNewTokenSchedule} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewTokenDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => createToken.mutate({ label: newTokenLabel, schedule: newTokenSchedule })}
              disabled={!newTokenLabel.trim() || createToken.isPending}
            >
              {createToken.isPending ? 'Criando...' : 'Criar Token'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Token criado — mostrar uma vez ── */}
      <Dialog open={!!createdToken} onOpenChange={() => setCreatedToken(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Token criado com sucesso!</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Copie o token abaixo. Por segurança, ele{' '}
              <strong>não poderá ser visualizado novamente</strong>.
            </p>
            <div className="flex items-center gap-2 bg-gray-50 border rounded-md p-3">
              <code className="text-sm flex-1 break-all text-blue-700">{createdToken}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(createdToken ?? '');
                  toast({ title: 'Token copiado!' });
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              Use este token durante a instalação do agente AROAN nos dispositivos.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setCreatedToken(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── EditAtivoDialog: aberto ao clicar em um dispositivo ── */}
      {editingAtivoId && ativoLoading && (
        <Dialog open onOpenChange={() => setEditingAtivoId(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Carregando ativo...</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center py-8 text-gray-400">
              <RefreshCw className="h-6 w-6 animate-spin mr-3" />
              Buscando dados do dispositivo...
            </div>
          </DialogContent>
        </Dialog>
      )}
      {editingAtivoData && (
        <EditAtivoDialog
          ativo={editingAtivoData}
          open
          onOpenChange={(o) => { if (!o) setEditingAtivoId(null); }}
        />
      )}
    </div>
  );
};

export default AROANDiscovery;
